import type { Entity } from "../../models"

/**
 * CHAPTER 0: the wreck beach (OLI-445, openspec ember/chapter-zero). A new character washes up alone on the
 * beach where the exiles' ship broke -- thrown overboard in the storm, they reach the shore after the others
 * were attacked and fled inland -- gets up, and walks off the beach into chapter one's clearing.
 *
 * The beach is an Ember camp with none of a camp's content, built per player on an authored map
 * (content/game/maps/rooms/ember/wreck-beach.tmj, from packages/tools/src/beach/build_map.py). Chapter 0 is a
 * value of the saved `emberChapter` flag.
 */
export const WRECK_BEACH_ROOM_ID = "wreck-beach"

/** The FEATURE_REGION strips along the beach's land edges: walking into one ends the chapter. */
export const CHAPTER_ZERO_EXIT_FEATURE = "chapterZeroExit"

/** How long a washed-up player lies there before getting up by themselves. */
export const WASHED_UP_MS = 5000

/**
 * How long getting up takes: the fall clip run backwards (the Ember `hurt` clip, 8 frames of 70 ms), with a little
 * over. The body is held still for it, on the server and in the client's own prediction: a body that moves while
 * the sprite is rising is cut out of the rise and drawn standing at once.
 */
export const WASHED_UP_RISE_MS = 600

export const isWreckBeachRoomId = (roomId: string | undefined): boolean =>
    !!roomId && (roomId === WRECK_BEACH_ROOM_ID || roomId.startsWith(`${WRECK_BEACH_ROOM_ID}-`))

/** Face down at the water's edge, until they try to move or WASHED_UP_MS passes. Not the downed state. */
export const washUp = (player: Entity, now: number): void => {
    player.washedUp = true
    player.washedUpUntil = now + WASHED_UP_MS
    player.risingUntil = undefined
}

/** Start getting up -- on a move, or when the timer runs out. False when they were not lying there. */
export const startRise = (player: Entity, now: number): boolean => {
    if (!player.washedUp) return false
    player.washedUp = false
    player.washedUpUntil = undefined
    player.risingUntil = now + WASHED_UP_RISE_MS
    return true
}

/**
 * A client's copy of the server's washed-up verdict. Letting go starts the rise here too, so the client's prediction
 * holds the legs for it as the server does. Every path that copies the verdict onto an entity goes through this:
 * the first to see it lift has to be the one that starts the rise.
 */
export const mirrorWashedUp = (entity: Entity, washedUp: boolean | undefined, now: number): void => {
    if (washedUp) entity.washedUp = true
    else if (!startRise(entity, now)) entity.washedUp = false
}

/** Whether a washed-up player's body is still held: lying there, or part way up. */
export const isHeldByWashUp = (player: Entity, now: number): boolean =>
    !!player.washedUp || (player.risingUntil ?? 0) > now
